import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_APP_VERSION, getCurrentAppVersion, getRequiredVersion, isVersionOutdated, testVersionComparison } from '../utils/versionUtils';
import { getLoggedInUser } from '../logic/loginLogic';
import { isUserAdmin } from '../utils/authUtils';

const VersionGate = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isOutdated, setIsOutdated] = useState(false);
  const [requiredVersion, setRequiredVersion] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Run version comparison test
    testVersionComparison();

    const checkVersionAndAdmin = async () => {
      try {
        setIsLoading(true);

        // Always check if user is admin first, regardless of route
        const user = getLoggedInUser();
        console.log('Checking user:', user?.uid);

        if (user) {
          try {
            const adminStatus = await isUserAdmin(user.uid);
            console.log('Admin status:', adminStatus, 'for user:', user.uid);
            setIsAdmin(adminStatus);

            // If user is admin, skip version check completely
            if (adminStatus) {
              console.log('Admin user detected, skipping version check');
              setIsOutdated(false);
              setIsLoading(false);
              return;
            } else {
              console.log('Non-admin user detected, proceeding with version check');
            }
          } catch (adminError) {
            console.error('Error checking admin status:', adminError);
            // If admin check fails, proceed with version check
            setIsAdmin(false);
          }
        } else {
          console.log('No user logged in, proceeding with version check');
          setIsAdmin(false);
        }

        // Only perform version check for non-admin users
        try {
          const current = await getCurrentAppVersion();
          const required = await getRequiredVersion();
          setRequiredVersion(required);

          const outdated = isVersionOutdated(current, required);
          console.log('Version check:', {
            current: current,
            required: required,
            outdated: outdated
          });
          setIsOutdated(outdated);

          if (outdated) {
            console.warn(`App version ${current} is outdated. Required version: ${required}`);
          }
        } catch (versionError) {
          console.error('Error checking version:', versionError);
          // If version check fails due to permissions, allow the app to run (fail-safe)
          // This handles the case where user is not authenticated yet
          console.log('Version check failed, allowing app to run (fail-safe)');
          setIsOutdated(false);
        }
      } catch (error) {
        console.error('Error in version gate:', error);
        // If any error occurs, allow the app to run (fail-safe)
        setIsOutdated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkVersionAndAdmin();
  }, [location.pathname]);

  // Show loading state - only show loading text on MenuPage
  if (isLoading) {
    const isMenuPage = location.pathname === '/menu';
    return (
      <>
        {children}
        {/* Show loading overlay only on MenuPage */}
        {isMenuPage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            color: 'white',
            fontSize: '18px',
            gap: '24px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              minWidth: '48px',
              minHeight: '48px',
              maxWidth: '48px',
              maxHeight: '48px',
              border: '4px solid rgba(230, 57, 57, 0.2)',
              borderTop: '4px solid #e63939',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              flexShrink: 0,
              aspectRatio: '1',
              boxSizing: 'border-box'
            }}></div>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>Chargement...</div>
          </div>
        )}
        {/* Admin indicator for debugging */}
        {isAdmin && (
          <div style={{
            position: 'fixed',
            bottom: '10px',
            right: '10px',
            backgroundColor: 'rgba(76, 175, 80, 0.9)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 9998,
            pointerEvents: 'none'
          }}>
            🔑 Admin Mode
          </div>
        )}
      </>
    );
  }

  // Show outdated version message
  if (isOutdated) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        color: 'white',
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '20px',
          color: '#ff6b6b'
        }}>
          ⚠️ Version Obsolète
        </div>

        <div style={{
          fontSize: '18px',
          marginBottom: '30px',
          lineHeight: '1.5',
          maxWidth: '400px'
        }}>
          Cette version de l'application est obsolète. Veuillez télécharger la nouvelle version.
        </div>

        <div style={{
          fontSize: '14px',
          marginBottom: '30px',
          color: '#cccccc'
        }}>
          Version actuelle: {DEFAULT_APP_VERSION}<br />
          Version requise: {requiredVersion}
        </div>

        <a
          href= "https://drive.usercontent.google.com/download?id=1Y_ShC5CYdKNwqSepEfaPr4nBIWUESMcm&export=download&authuser=0"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '12px 24px',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: 'bold'
          }}
        >
          Télécharger
        </a>


        <div style={{
          fontSize: '12px',
          marginTop: '20px',
          color: '#888888',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          <p>📱 <strong>Instructions:</strong></p>
          <p>• Cliquez sur le bouton ci-dessus</p>
          <p>• Le lien s'ouvrira dans votre navigateur</p>
          <p>• Téléchargez l'APK depuis la page Google Drive</p>
          <p style={{ marginTop: '10px', fontSize: '11px' }}>
            Si le lien ne fonctionne pas, veuillez contacter le support.
          </p>
        </div>

        {/* Admin bypass indicator */}
        {isAdmin && (
          <div style={{
            fontSize: '10px',
            marginTop: '10px',
            color: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            padding: '5px 10px',
            borderRadius: '4px',
            border: '1px solid #4CAF50'
          }}>
            🔑 Accès admin détecté - Version check contourné
          </div>
        )}
      </div>
    );
  }

  // Version is up to date, render children
  return (
    <>
      {children}
      {/* Admin indicator for debugging */}
      {isAdmin && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          backgroundColor: 'rgba(76, 175, 80, 0.9)',
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 9998,
          pointerEvents: 'none'
        }}>
          🔑 Admin Mode
        </div>
      )}
    </>
  );
};

export default VersionGate; 